/**
 * The scope picker's model, built from GET /api/organisations/scopes.
 *
 * Owns turning the payload into what the sidebar lists (whole school, or
 * all students for a tutor; leaving years, custom groups, forms and classes
 * by subject, per organisation) and finding which of those a scope string names. Scope
 * strings are the server's own (org:<id> | cohort:<id>:<year> | group:<id>,
 * which a form or class uses too) and are never built here.
 *
 * Entry kinds: 'organisation', 'cohort', 'form', 'class' and 'group' (a
 * custom group). Forms and classes are imported from the school's records,
 * so only custom groups are offered for adding, removing and deleting.
 *
 * Deliberately does not decide who sees what: the server returns only the
 * organisations the viewer may manage (every one for a platform admin).
 */
import { leavingLabel, yearGroupFor } from '../../organisations/lib/schoolYears';

const CUSTOM_GROUP = 'custom';

const ADMISSIONS_TESTS = ['LNAT', 'TARA', 'TMUA', 'ESAT', 'UCAT'];
const CAREERS = [
  'Medicine', 'Law', 'Engineering', 'Software', 'Finance', 'Research',
  'Architecture', 'Business', 'Public Sector', 'Undecided',
];

const namedAt = (names, name) => names.findIndex((item) => item.toLowerCase() === String(name).toLowerCase());

/** Oxbridge, then an admissions test, then a career, then any other custom group by name. */
export function compareCustomGroups(a, b) {
  const rank = (name) => {
    if (/oxbridge/i.test(name)) return [0, 0, ''];
    const exam = namedAt(ADMISSIONS_TESTS, name);
    if (exam >= 0) return [1, exam, ''];
    const career = namedAt(CAREERS, name);
    if (career >= 0) return [2, career, ''];
    return [3, 0, String(name).toLowerCase()];
  };
  const left = rank(a.name);
  const right = rank(b.name);
  return left[0] - right[0] || left[1] - right[1] || left[2].localeCompare(right[2]);
}

const groupEntry = (kind) => (g) => ({
  kind, scope: g.scope, id: g.id, label: g.name, count: g.student_count, subject: g.subject,
});

/**
 * Schools have leaving years; a tutor's students never get one. A payload
 * without `org_type` (a server from before tutors) is a school.
 */
const isSchool = (organisation) => (organisation?.org_type ?? 'school') === 'school';

/** One sidebar section per organisation. `today` is londonToday(). */
export function buildScopeSections(payload, today) {
  return (payload?.organisations || []).map((entry) => ({
    organisation: entry.organisation,
    entries: [
      {
        kind: 'organisation',
        scope: entry.scope,
        label: isSchool(entry.organisation) ? 'Whole school' : 'All students',
        count: entry.student_count,
      },
      ...entry.cohorts.map((c) => ({
        kind: 'cohort',
        scope: c.scope,
        label: leavingLabel(c.cohort_year, yearGroupFor(c.cohort_year, today)),
        count: c.student_count,
      })),
    ],
    // Only a school asks for a leaving year, so only a school has students missing one.
    studentsWithoutCohort: isSchool(entry.organisation) ? entry.students_without_cohort : 0,
    // `|| []`: a server from before classes sends neither key.
    forms: (entry.forms || []).map(groupEntry('form')),
    subjects: (entry.classes || []).map((s) => ({
      subject: s.subject,
      classes: s.classes.map(groupEntry('class')),
    })),
    groups: entry.groups.filter((g) => g.group_type === CUSTOM_GROUP)
      .sort(compareCustomGroups)
      .map(groupEntry('group')),
  }));
}

const sectionEntries = (section) => [
  ...section.entries,
  ...section.groups,
  ...section.forms,
  ...section.subjects.flatMap((s) => s.classes),
];

/** The entry and its section for a scope string, or null if it names none. */
export function findScope(sections, scope) {
  for (const section of sections) {
    const entry = sectionEntries(section).find((e) => e.scope === scope);
    if (entry) return { section, entry };
  }
  return null;
}
